import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, CircularProgress, FormControl,
  InputLabel, Select, MenuItem, FormHelperText,
  Grid, Chip, Typography, Switch, FormControlLabel,
  Paper, Box
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { id } from 'date-fns/locale';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import MasterJobSelector from './MasterJobSelector';
import refreshGanttChart from './GanttRefresher';
import TaskSplitDialog from './TaskSplitDialog';
import { t } from '../../utils/textUtils';

// Helper function for consistent duration calculation across the application
const calculateDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return 1;
  
  // Normalize dates to midnight to avoid timezone issues
  let start = new Date(startDate);
  let end = new Date(endDate);
  
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  
  const diffTime = Math.abs(end - start);
  const baseDuration = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const duration = baseDuration + 1; // Include both start and end dates
  
  console.log(`TaskForm calculateDuration: ${start.toISOString()} to ${end.toISOString()} = ${duration} days`);
  return duration > 0 ? duration : 1;
};

// Constants
const DEPENDENCY_TYPES = [
  { value: 'FS', label: t('SELESAI KE MULAI (FS)'), description: t('TUGAS B DIMULAI SETELAH TUGAS A SELESAI') },
  { value: 'FF', label: t('SELESAI KE SELESAI (FF)'), description: t('TUGAS B SELESAI SETELAH TUGAS A SELESAI') },
  { value: 'SS', label: t('MULAI KE MULAI (SS)'), description: t('TUGAS B DIMULAI SETELAH TUGAS A DIMULAI') },
  { value: 'SF', label: t('MULAI KE SELESAI (SF)'), description: t('TUGAS B SELESAI SETELAH TUGAS A DIMULAI') }
];

const UNITS = [
  { value: 'm3', label: t('M³ (KUBIK)') },
  { value: 'm2', label: t('M² (PERSEGI METER)') },
  { value: 'm', label: t('M (METER LINIER)') },
  { value: 'ls', label: t('LS (LUMPSUM)') },
  { value: 'unit', label: t('UNIT') },
  { value: 'pcs', label: t('PCS') },
  { value: 'set', label: t('SET') },
  { value: 'titik', label: t('TITIK') },
  { value: 'EA', label: t('EA (EACH)') }
];

// Initial form state
const initialFormState = {
  name: '',
  startDate: new Date(),
  endDate: new Date(new Date().setDate(new Date().getDate() + 1)),
  progress: 0,
  quantity: 0,
  completedQuantity: 0,
  unit: '',
  unitPrice: 0, // Harga satuan dari BOQ
  autoCalculateWeight: false, // Flag untuk menghitung bobot otomatis
  weight: 0,
  notes: '',
  assignedTo: '',
  displayOrder: 0,
  isMilestone: false,
  dependencies: [],
  masterJobId: null,
  jobCode: '',
  status: 'to_do'
};

const TaskForm = ({ open, onClose, workCategoryId, task, onSave, projectId }) => {
  // State
  const [formData, setFormData] = useState({ ...initialFormState });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [subcontractors, setSubcontractors] = useState([]);
  const [loadingSubcontractors, setLoadingSubcontractors] = useState(false);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [loadingAvailableTasks, setLoadingAvailableTasks] = useState(false);
  const [workCategory, setWorkCategory] = useState(null);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  
  const { enqueueSnackbar } = useSnackbar();
  
  // Helper functions
  const calculateProgress = useCallback((completed, total, weight) => {
    if (!total || total <= 0 || !weight) return 0;
    const rawProgress = (completed / total) * weight;
    return Math.min(rawProgress, weight); // Progress can't exceed weight
  }, []);
  
  const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };
  
  // API calls
  const fetchWorkCategory = useCallback(async () => {
    if (!workCategoryId) return;
    
    try {
      const response = await api.get(`/work-categories/${workCategoryId}`);
      if (response.data && response.data.success) {
        setWorkCategory(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching work category:', error);
    }
  }, [workCategoryId]);
  
  const fetchSubcontractors = useCallback(async () => {
    try {
      setLoadingSubcontractors(true);
      const response = await api.get('/installation-teams');
      if (response.data && Array.isArray(response.data.installationTeams)) {
        setSubcontractors(response.data.installationTeams);
      }
    } catch (error) {
      console.error('Error fetching subcontractors:', error);
      enqueueSnackbar(t('GAGAL MEMUAT DATA SUBKONTRAKTOR'), { variant: 'error' });
    } finally {
      setLoadingSubcontractors(false);
    }
  }, [enqueueSnackbar]);
  
  const fetchAvailableTasks = useCallback(async () => {
    if (!projectId) return;
    
    try {
      setLoadingAvailableTasks(true);
      const response = await api.get(`/projects/${projectId}/gantt/flat`);
      
      if (response.data?.success && Array.isArray(response.data.data?.data)) {
        const tasks = response.data.data.data
          .filter(t => t.type === 'task' && (!task || t.id !== task.id))
          .map(t => ({
            id: t.id,
            name: t.text,
            startDate: t.start_date,
            endDate: t.end_date
          }));
        
        setAvailableTasks(tasks);
      } else {
        console.error('Unexpected API response format:', response.data);
      }
    } catch (error) {
      console.error('Error fetching available tasks:', error);
    } finally {
      setLoadingAvailableTasks(false);
    }
  }, [projectId, task]);
  
  // Event handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const updatedData = { ...prev, [name]: value };
      
      // Auto-calculate progress when completedQuantity changes
      if (name === 'completedQuantity') {
        const newProgress = calculateProgress(
          parseFloat(value) || 0, 
          updatedData.quantity || 0, 
          updatedData.weight || 0
        );
        updatedData.progress = newProgress * 100 / (updatedData.weight || 1);
      }
      
      // Auto-calculate completedQuantity when progress changes
      if (name === 'progress') {
        const progressDecimal = parseFloat(value) / 100;
        const weightDecimal = updatedData.weight / 100;
        const ratio = weightDecimal > 0 ? progressDecimal / weightDecimal : 0;
        updatedData.completedQuantity = ratio * updatedData.quantity;
      }
      
      return updatedData;
    });
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };
  
  const handleDateChange = (name, date) => {
    setFormData(prev => ({ ...prev, [name]: date }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };
  
  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };
  
  const handleMasterJobChange = async (masterJobId) => {
    setFormData(prev => ({ ...prev, masterJobId }));
    
    if (masterJobId) {
      try {
        const response = await api.get(`/master-jobs/${masterJobId}`);
        
        if (response.data?.success) {
          const masterJob = response.data.data;
          
          setFormData(prev => ({
            ...prev,
            name: masterJob.nama_pekerjaan,
            quantity: masterJob.volume_default || 0,
            unit: masterJob.satuan || '',
            jobCode: masterJob.kode_pekerjaan || ''
          }));
          
          if (errors.name) {
            setErrors(prev => ({ ...prev, name: null }));
          }
        }
      } catch (error) {
        console.error('Error fetching master job details:', error);
        enqueueSnackbar(t('GAGAL MEMUAT DETAIL PEKERJAAN'), { variant: 'error' });
      }
    } else {
      setFormData(prev => ({ ...prev, jobCode: '', name: '' }));
    }
  };
  
  const handleDependencyAdd = (taskId, type, lag) => {
    setFormData(prev => ({
      ...prev,
      dependencies: [...prev.dependencies, { taskId, type, lag }]
    }));
  };
  
  const handleDependencyRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      dependencies: prev.dependencies.filter((_, i) => i !== index)
    }));
  };
  
  const handleDependencyChange = (index, field, value) => {
    setFormData(prev => {
      const newDependencies = [...prev.dependencies];
      newDependencies[index] = { ...newDependencies[index], [field]: value };
      
      return { ...prev, dependencies: newDependencies };
    });
  };
  
  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name) newErrors.name = t('NAMA PEKERJAAN WAJIB DIISI');
    if (!formData.startDate) newErrors.startDate = t('TANGGAL MULAI WAJIB DIISI');
    if (!formData.endDate) newErrors.endDate = t('TANGGAL SELESAI WAJIB DIISI');
    
    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      newErrors.endDate = t('TANGGAL SELESAI HARUS SETELAH TANGGAL MULAI');
    }
    
    if (formData.progress < 0 || formData.progress > 100) {
      newErrors.progress = t('PROGRESS HARUS ANTARA 0-100%');
    }
    
    if (formData.quantity < 0) newErrors.quantity = t('QUANTITY TIDAK BOLEH NEGATIF');
    
    if (formData.weight < 0 || formData.weight > 100) {
      newErrors.weight = t('BOBOT HARUS ANTARA 0-100%');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle split dialog
  const handleOpenSplitDialog = () => {
    setSplitDialogOpen(true);
  };
  
  const handleCloseSplitDialog = () => {
    setSplitDialogOpen(false);
  };
  
  const handleTaskSplit = async (splitData) => {
    try {
      const { splitDate, resumeDate, splitProgress, reason } = splitData;
      
      // Call API to split task
      const response = await api.post(`/projects/${projectId}/gantt/tasks/${task.id}/split`, {
        splitDate,
        resumeDate,
        splitProgress: splitProgress / 100, // Convert percentage to decimal
        reason
      });

      if (response.data.success) {
        enqueueSnackbar('Task berhasil di-split', { variant: 'success' });
        handleCloseSplitDialog();
        onClose(); // Close the edit dialog
        if (onSave) onSave(); // Refresh the gantt chart
      } else {
        throw new Error(response.data.message || 'Failed to split task');
      }

    } catch (error) {
      console.error('Error splitting task:', error);
      enqueueSnackbar(`Gagal split task: ${error.message}`, { variant: 'error' });
    }
  };
  
  // Form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      // Validate required IDs
      if (!workCategoryId) {
        enqueueSnackbar(t('ERROR: ID KATEGORI PEKERJAAN TIDAK ADA'), { variant: 'error' });
        setLoading(false);
        return;
      }
      
      if (isNaN(parseInt(workCategoryId))) {
        enqueueSnackbar(t('ERROR: ID KATEGORI PEKERJAAN TIDAK VALID'), { variant: 'error' });
        setLoading(false);
        return;
      }
      
      if (!projectId) {
        enqueueSnackbar(t('ERROR: ID PROYEK TIDAK ADA'), { variant: 'error' });
        setLoading(false);
        return;
      }
      
      // Fetch master job details if needed
      if (formData.masterJobId && !formData.name) {
        try {
          const response = await api.get(`/master-jobs/${formData.masterJobId}`);
          if (response.data?.success) {
            const masterJob = response.data.data;
            formData.name = masterJob.nama_pekerjaan;
            formData.jobCode = masterJob.kode_pekerjaan;
          }
        } catch (err) {
          console.error('Error re-fetching master job details:', err);
        }
      }
      
      // Prepare payload
      const startDate = formatDate(formData.startDate);
      const endDate = formatDate(formData.endDate);
      
      // Calculate duration using the consistent helper function
      const duration = calculateDuration(formData.startDate, formData.endDate);
      
      const payload = {
        name: formData.name,
        startDate: startDate,
        endDate: endDate,
        duration: duration, // Explicitly include calculated duration
        progress: formData.progress,
        quantity: formData.quantity,
        completedQuantity: formData.completedQuantity,
        unit: formData.unit,
        unitPrice: formData.unitPrice, // Harga satuan dari BOQ
        autoCalculateWeight: formData.autoCalculateWeight, // Flag untuk menghitung bobot otomatis
        weight: formData.weight,
        notes: formData.notes,
        assignedTo: formData.assignedTo || null,
        dependencies: Array.isArray(formData.dependencies) ? formData.dependencies : [],
        workCategoryId,
        isMilestone: formData.isMilestone,
        displayOrder: formData.displayOrder,
        masterJobId: formData.masterJobId || null,
        jobCode: formData.jobCode || null,
        status: formData.status || 'to_do'
      };
      
      console.log(`TaskForm submit payload: startDate=${startDate}, endDate=${endDate}, duration=${duration} days`);
      
      // Final validation
      if (!payload.name) {
        enqueueSnackbar(t('ERROR: NAMA PEKERJAAN WAJIB DIISI'), { variant: 'error' });
        setErrors(prev => ({ ...prev, name: 'Work item name is required' }));
        setLoading(false);
        return;
      }
      
      // Send API request
      let response;
      try {
        if (task) {
          response = await api.put(`/tasks/${task.id}`, payload);
        } else {
          response = await api.post(`/work-categories/${workCategoryId}/tasks`, payload);
        }
        
        // Handle successful response
        if (response.data) {
          const savedTask = response.data.data || response.data.task || response.data;
          
          enqueueSnackbar(task ? 'Task updated successfully' : 'Task created successfully', { variant: 'success' });
          onClose();
          
          // Refresh Gantt chart
          window.dispatchEvent(new CustomEvent('FORCE_GANTT_REFRESH', { 
            detail: { 
              projectId, 
              timestamp: Date.now(),
              taskId: savedTask.id
            } 
          }));
          
          // Additional refresh with delay
          setTimeout(() => {
            refreshGanttChart(projectId);
            
            if (typeof onSave === 'function') {
              onSave(savedTask);
            }
          }, 1000);
        } else {
          enqueueSnackbar(t('FORMAT RESPON SERVER TIDAK DIKENALI'), { variant: 'error' });
        }
      } catch (apiError) {
        // Handle API errors
        if (apiError.response) {
          const errorMessage = apiError.response.data.message || 
                              apiError.response.data.error || 
                              'Unknown server error';
          
          enqueueSnackbar(t(`ERROR ${apiError.response.status}: ${errorMessage}`), { variant: 'error' });
        } else if (apiError.request) {
          enqueueSnackbar('No response from server. Please check that the backend is running.', { variant: 'error' });
        } else {
          enqueueSnackbar(`Error: ${apiError.message || 'Unknown error'}`, { variant: 'error' });
        }
      }
    } catch (error) {
      enqueueSnackbar(`Error: ${error.message || 'An unexpected error occurred'}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // Initialize form data when dialog opens
  useEffect(() => {
    if (open) {
      fetchSubcontractors();
      fetchAvailableTasks();
      fetchWorkCategory();
      
      if (task) {
        // For existing tasks, add completed quantity
        const completedQuantity = task.completedQuantity || (task.progress / 100) * (task.quantity || 0);
        
        setFormData({
          name: task.name || '',
          startDate: task.startDate ? new Date(task.startDate) : new Date(),
          endDate: task.endDate ? new Date(task.endDate) : new Date(new Date().setDate(new Date().getDate() + 1)),
          progress: task.progress || 0,
          quantity: task.quantity || 0,
          completedQuantity: completedQuantity,
          unit: task.unit || '',
          weight: task.weight || 0,
          notes: task.notes || '',
          assignedTo: task.assignedTo || '',
          displayOrder: task.displayOrder || 0,
          isMilestone: task.isMilestone || false,
          dependencies: task.dependencies || [],
          masterJobId: task.masterJobId || null,
          jobCode: task.jobCode || ''
        });
      } else {
        setFormData({ ...initialFormState });
      }
      
      setErrors({});
    }
  }, [task, open, workCategoryId, fetchAvailableTasks, fetchSubcontractors, fetchWorkCategory]);
  
  // Calculate derived values
  const durationDays = useMemo(() => {
    if (!formData.startDate || !formData.endDate) return 1;
    return calculateDuration(formData.startDate, formData.endDate);
  }, [formData.startDate, formData.endDate]);
  
  // Calculate total cost
  const totalCost = useMemo(() => {
    return formData.quantity * formData.unitPrice;
  }, [formData.quantity, formData.unitPrice]);
  
  // useEffect untuk memperbarui durasi secara real-time menggunakan helper function
  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      const calculatedDuration = calculateDuration(formData.startDate, formData.endDate);
      
      // Update formData dengan durasi yang benar
      setFormData(prev => ({
        ...prev,
        duration: calculatedDuration
      }));
    }
  }, [formData.startDate, formData.endDate]);
  
  // Render form
  return (
    <>
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid #e0e0e0', 
        bgcolor: '#2196f3', // Blue background color
        color: 'white',     // White text color
        py: 2,
        fontWeight: 600
      }}>
        {task ? 'Edit Task' : 'Add New Task'}
      </DialogTitle>
      
      <DialogContent sx={{ px: 3, py: 2 }}>
        <Grid container spacing={3}>
          {/* Master Job Selection Section */}
          <Grid item xs={12}>
            <Paper 
              elevation={0}
              variant="outlined" 
              sx={{ 
                p: 2.5, 
                mb: 1, 
                bgcolor: '#f9f9ff', 
                border: '1px solid #e0e0ff',
                borderRadius: 1.5
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#3f51b5' }}>
                  Master Work Item Reference
                </Typography>
                {formData.jobCode && (
                  <Chip 
                    label={formData.jobCode} 
                    color="primary" 
                    size="small" 
                    sx={{ ml: 2 }}
                  />
                )}
              </Box>
              
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Start by selecting a Master Work Item to automatically populate the task details
              </Typography>
              
              <MasterJobSelector 
                value={formData.masterJobId} 
                onChange={handleMasterJobChange}
                kategori={workCategory ? workCategory.name : null}
              />
            </Paper>
          </Grid>
          
          {/* Task Details Section */}
          <Grid item xs={12}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mt: 1, 
              mb: 2,
              '&::before, &::after': {
                content: '""',
                flex: 1,
                borderBottom: '1px solid #e0e0e0'
              }
            }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  px: 2, 
                  color: 'text.secondary', 
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                Work Item Details
              </Typography>
            </Box>
            
            <Paper 
              elevation={0} 
              variant="outlined" 
              sx={{ p: 2.5, borderRadius: 1.5, mb: 3 }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="Work Item Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    fullWidth
                    required
                    error={!!errors.name}
                    helperText={formData.masterJobId !== null ?
                      "Name is automatically set from the selected Master Work Item" :
                      (errors.name || "Enter work item name or select a Master Work Item")}
                    disabled={formData.masterJobId !== null}
                    InputProps={{
                      readOnly: formData.masterJobId !== null,
                      style: formData.masterJobId !== null ? { backgroundColor: '#f5f5f5' } : {}
                    }}
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={formData.isMilestone} 
                        onChange={handleSwitchChange} 
                        name="isMilestone"
                        color="primary"
                      />
                    }
                    label="Milestone"
                    sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
                  />
                </Grid>
                
                {/* Display Order is now automatically handled by backend */}
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Durasi (hari)"
                    name="duration"
                    type="number"
                    value={durationDays}
                    onChange={(e) => {
                      const newDur = parseInt(e.target.value, 10) || 1;
                      // Recalculate endDate = startDate + (newDur - 1)
                      const start = new Date(formData.startDate);
                      start.setHours(0, 0, 0, 0);
                      const newEnd = new Date(start);
                      newEnd.setDate(start.getDate() + newDur - 1);
                      handleDateChange('endDate', newEnd);
                    }}
                    InputProps={{
                      inputProps: { min: 1 }
                    }}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          {/* Time Period Section */}
          <Grid item xs={12}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2,
              '&::before, &::after': {
                content: '""',
                flex: 1,
                borderBottom: '1px solid #e0e0e0'
              }
            }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  px: 2, 
                  color: 'text.secondary', 
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                Periode Waktu
              </Typography>
            </Box>

            <Paper 
              elevation={0} 
              variant="outlined" 
              sx={{ p: 2.5, borderRadius: 1.5, mb: 3 }}
            >
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
                Periode Waktu Pelaksanaan
              </Typography>
              
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                flexWrap: 'wrap',
                gap: 2,
                mb: 2
              }}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={id}>
                  <DatePicker
                    label="Tanggal Mulai"
                    value={formData.startDate}
                    onChange={(date) => handleDateChange('startDate', date)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!errors.startDate,
                        helperText: errors.startDate,
                        size: "small",
                        sx: { minWidth: 160 }
                      }
                    }}
                    format="dd MMM yyyy"
                  />
                </LocalizationProvider>
                
                <Typography variant="body2" sx={{ mx: 0.5 }}>
                  sampai dengan
                </Typography>
                
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={id}>
                  <DatePicker
                    label="Tanggal Selesai"
                    value={formData.endDate}
                    onChange={(date) => handleDateChange('endDate', date)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!errors.endDate,
                        helperText: errors.endDate,
                        size: "small",
                        sx: { minWidth: 160 }
                      }
                    }}
                    format="dd MMM yyyy"
                  />
                </LocalizationProvider>
                
                
              <Typography variant="body2" sx={{ ml: 2 }}>
                Hari {`${new Date(formData.endDate).getDate()} ${
                  new Date(formData.endDate).toLocaleString('id', { month: 'long' })
                } ${new Date(formData.endDate).getFullYear()}`}
              </Typography>
              </Box>
              
              {/* Quick duration buttons */}
              {/* <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="body2" sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                  Set Durasi:
                </Typography>
                
                {[
                  { days: 1, label: '1 Hari' },
                  { days: 2, label: '2 Hari' },
                  { days: 3, label: '3 Hari' },
                  { days: 7, label: '1 Minggu' },
                  { days: 14, label: '2 Minggu' },
                  { days: 30, label: '1 Bulan' }
                ].map(option => (
                  <Button 
                    key={option.days}
                    variant="outlined" 
                    size="small"
                    color="secondary"
                    onClick={() => {
                      const end = new Date(formData.startDate);
                      end.setDate(end.getDate() + option.days);
                      handleDateChange('endDate', end);
                    }}
                    sx={{ minWidth: 'auto', px: 1.5 }}
                  >
                    {option.label}
                  </Button>
                ))}
              </Box> */}
            </Paper>
          </Grid>
          
          {/* Quantity & Progress Section */}
          <Grid item xs={12}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2,
              '&::before, &::after': {
                content: '""',
                flex: 1,
                borderBottom: '1px solid #e0e0e0'
              }
            }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  px: 2, 
                  color: 'text.secondary', 
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                Work Item Quantity & Progress
              </Typography>
            </Box>
            
            <Paper 
              elevation={0} 
              variant="outlined" 
              sx={{ p: 2.5, borderRadius: 1.5, mb: 3 }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Total Quantity"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    type="number"
                    fullWidth
                    InputProps={{ 
                      inputProps: { min: 0, step: 0.01 },
                      endAdornment: formData.unit ? (
                        <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                          {formData.unit}
                        </Typography>
                      ) : null
                    }}
                    error={!!errors.quantity}
                    helperText={errors.quantity || "Total work volume"}
                    size="small"
                  />
                </Grid>
                
                {/* <Grid item xs={12} sm={4}>
                  <TextField
                    label="Completed Quantity"
                    name="completedQuantity"
                    value={formData.completedQuantity}
                    onChange={handleChange}
                    type="number"
                    fullWidth
                    InputProps={{ 
                      inputProps: { 
                        min: 0, 
                        max: formData.quantity > 0 ? formData.quantity : undefined,
                        step: 0.01
                      },
                      endAdornment: formData.unit ? (
                        <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                          {formData.unit}
                        </Typography>
                      ) : null
                    }}
                    error={!!errors.completedQuantity}
                    helperText={errors.completedQuantity || 
                      `Work volume completed so far (${formData.quantity > 0 ? 
                        ((formData.completedQuantity / formData.quantity) * 100).toFixed(1) : 0}%)`}
                    size="small"
                  />
                </Grid> */}
                
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Unit</InputLabel>
                    <Select
                      label="Unit"
                      name="unit"
                      value={formData.unit}
                      onChange={handleChange}
                    >
                      {UNITS.map((unit) => (
                        <MenuItem key={unit.value} value={unit.value}>
                          {unit.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>Unit of measurement</FormHelperText>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Unit Price"
                    name="unitPrice"
                    value={formData.unitPrice}
                    onChange={handleChange}
                    type="number"
                    fullWidth
                    InputProps={{ 
                      inputProps: { min: 0, step: 1000 },
                      startAdornment: <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary' }}>Rp</Typography>
                    }}
                    error={!!errors.unitPrice}
                    helperText={errors.unitPrice || "Harga satuan dari BOQ"}
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Weight (%)"
                    name="weight"
                    value={formData.weight}
                    onChange={handleChange}
                    type="number"
                    fullWidth
                    InputProps={{ 
                      inputProps: { min: 0, max: 100, step: 0.01 },
                      endAdornment: <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>%</Typography>
                    }}
                    error={!!errors.weight}
                    helperText={errors.weight || "Bobot pekerjaan terhadap total project (0-100%)"}
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        border: '1px dashed rgba(25, 118, 210, 0.5)',
                        borderRadius: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          Total Cost:
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, ml: 1 }}>
                          {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(totalCost)}
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <Box sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    justifyContent: 'flex-start', 
                    pt: 1
                  }}>
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={formData.autoCalculateWeight} 
                          onChange={handleSwitchChange} 
                          name="autoCalculateWeight"
                          color="primary"
                        />
                      }
                      label="Hitung Bobot Otomatis"
                    />
                    <FormHelperText sx={{ ml: 2 }}>
                      {formData.autoCalculateWeight ? 
                        "Bobot dihitung otomatis berdasarkan volume × harga" : 
                        "Aktifkan untuk menghitung bobot secara otomatis"}
                    </FormHelperText>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Weight (%)"
                    name="weight"
                    value={formData.weight}
                    onChange={handleChange}
                    type="number"
                    fullWidth
                    InputProps={{ 
                      inputProps: { min: 0, max: 100, step: 0.01 },
                      endAdornment: <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>%</Typography>,
                      readOnly: formData.autoCalculateWeight
                    }}
                    disabled={formData.autoCalculateWeight}
                    error={!!errors.weight}
                    helperText={formData.autoCalculateWeight ? "Bobot dihitung otomatis berdasarkan volume × harga" : "Weight percentage for S-curve calculation"}
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Progress (%)"
                    name="progress"
                    value={formData.progress}
                    onChange={handleChange}
                    type="number"
                    fullWidth
                    InputProps={{ 
                      inputProps: { min: 0, max: 100, step: 0.01 },
                      endAdornment: <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>%</Typography>
                    }}
                    error={!!errors.progress}
                    helperText={errors.progress || 
                      `Contribution to project: ${(formData.progress / 100 * formData.weight).toFixed(2)}%`}
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      bgcolor: '#f5f9ff', 
                      height: '100%', 
                      display: 'flex', 
                      alignItems: 'center',
                      borderRadius: 1
                    }}
                  >
                    <Typography variant="body2">
                      <strong>S-Curve Impact:</strong> {formData.quantity > 0 && formData.weight > 0 
                        ? `Each 1 ${formData.unit} = ${(formData.weight / formData.quantity).toFixed(4)}% of project` 
                        : 'Set quantity and weight to see impact'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          {/* Subcontractor & Notes Section */}
          <Grid item xs={12}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2,
              '&::before, &::after': {
                content: '""',
                flex: 1,
                borderBottom: '1px solid #e0e0e0'
              }
            }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  px: 2, 
                  color: 'text.secondary', 
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                Subcontractor & Notes
              </Typography>
            </Box>
            
            <Paper 
              elevation={0} 
              variant="outlined" 
              sx={{ p: 2.5, borderRadius: 1.5, mb: 3 }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Subcontractor</InputLabel>
                    <Select
                      label="Subcontractor"
                      name="assignedTo"
                      value={formData.assignedTo}
                      onChange={handleChange}
                      disabled={loadingSubcontractors}
                      startAdornment={
                        loadingSubcontractors ? <CircularProgress size={20} color="inherit" sx={{ mr:1 }} /> : null
                      }
                    >
                      <MenuItem value="">-- None --</MenuItem>
                      {subcontractors.map(sc => (
                        <MenuItem key={sc.id} value={sc.id}>{sc.name}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {loadingSubcontractors ? 'Loading subcontractors...' : 'Select a subcontractor for this task'}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          {/* Split Task Section - Only show for existing tasks */}
          {console.log('TaskForm - task prop:', task)}
          {task && (
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 2,
                '&::before, &::after': {
                  content: '""',
                  flex: 1,
                  borderBottom: '1px solid #e0e0e0'
                }
              }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    px: 2, 
                    color: 'text.secondary', 
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Split Task
                </Typography>
              </Box>
              
              <Paper 
                elevation={0} 
                variant="outlined" 
                sx={{ p: 2.5, borderRadius: 1.5, mb: 3 }}
              >
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                  Split Task into Two Parts
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Use this feature to split a task that needs to be paused and resumed later. 
                  For example: "pekerjaan bongkar" → "pekerjaan bongkar 1" + "pekerjaan bongkar 2"
                </Typography>
                
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => {
                    // We'll implement this function
                    handleOpenSplitDialog();
                  }}
                  sx={{ 
                    borderColor: '#ff9800',
                    color: '#ff9800',
                    '&:hover': {
                      borderColor: '#f57c00',
                      backgroundColor: 'rgba(255, 152, 0, 0.04)'
                    }
                  }}
                >
                  Split This Task
                </Button>
                
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                  This will create two separate tasks with sequential naming (e.g., Task 1, Task 2)
                </Typography>
              </Paper>
            </Grid>
          )}
          
          {/* Task Status Section */}
          {/* <Grid item xs={12}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2,
              '&::before, &::after': {
                content: '""',
                flex: 1,
                borderBottom: '1px solid #e0e0e0'
              }
            }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  px: 2, 
                  color: 'text.secondary', 
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                Task Status
              </Typography>
            </Box>
            
            <Paper 
              elevation={0} 
              variant="outlined" 
              sx={{ p: 2.5, borderRadius: 1.5, mb: 3 }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                    >
                      <MenuItem value="to_do">To Do</MenuItem>
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="done">Done</MenuItem>
                    </Select>
                    <FormHelperText>Current status of the task</FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          </Grid> */}
          
          {/* Dependencies Section */}
          <Grid item xs={12}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2,
              '&::before, &::after': {
                content: '""',
                flex: 1,
                borderBottom: '1px solid #e0e0e0'
              }
            }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  px: 2, 
                  color: 'text.secondary', 
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                Dependencies
              </Typography>
            </Box>
            
            <Paper 
              elevation={0} 
              variant="outlined" 
              sx={{ p: 2.5, borderRadius: 1.5 }}
            >
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500 }}>
                Dependencies (Predecessors)
              </Typography>
              
              {/* Existing dependencies */}
              {formData.dependencies && formData.dependencies.length > 0 ? (
                <Box sx={{ mb: 2 }}>
                  {formData.dependencies.map((dep, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        mb: 1, 
                        p: 1.5, 
                        borderRadius: 1,
                        bgcolor: '#f5f5f5',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                        {availableTasks.find(t => t.id === dep.taskId)?.name || `Task ${dep.taskId}`}
                      </Typography>
                      
                      <Chip 
                        label={dep.type || 'FS'} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ mx: 1 }}
                      />
                      
                      <Chip 
                        label={`Lag: ${dep.lag || 0} days`} 
                        size="small" 
                        color="secondary" 
                        variant="outlined"
                        sx={{ mr: 1 }}
                      />
                      
                      <Button 
                        size="small" 
                        color="error" 
                        variant="outlined"
                        onClick={() => handleDependencyRemove(index)}
                        sx={{ minWidth: 'auto' }}
                      >
                        Remove
                      </Button>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box 
                  sx={{ 
                    mb: 2, 
                    p: 2, 
                    bgcolor: '#f9f9f9', 
                    borderRadius: 1,
                    border: '1px dashed #ccc'
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                    No dependencies added yet. Add a dependency to specify work items that must be completed before this one.
                  </Typography>
                </Box>
              )}
              
              {/* Add dependency form */}
              <Paper 
                variant="outlined" 
                sx={{ p: 2, borderRadius: 1, bgcolor: '#fafafa' }}
              >
                <Typography variant="subtitle2" gutterBottom sx={{ mb: 1.5, fontWeight: 500 }}>
                  Add New Dependency
                </Typography>
                
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Predecessor Task</InputLabel>
                      <Select
                        label="Predecessor Task"
                        id="dependency-task-select"
                        value=""
                        onChange={(e) => {
                          if (e.target.value) {
                            handleDependencyAdd(e.target.value, 'FS', 0);
                            e.target.value = ""; // Reset select after adding
                          }
                        }}
                        disabled={loadingAvailableTasks}
                        startAdornment={
                          loadingAvailableTasks ? (
                            <CircularProgress size={20} color="inherit" style={{ marginRight: 8 }} />
                          ) : null
                        }
                      >
                        <MenuItem value="">-- Select Task --</MenuItem>
                        {availableTasks.map((task) => (
                          <MenuItem key={task.id} value={task.id}>
                            {task.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {loadingAvailableTasks ? 'Loading available tasks...' : 'Select a task that must be completed before this one'}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Type</InputLabel>
                      <Select
                        label="Type"
                        defaultValue="FS"
                        onChange={(e) => {
                          if (formData.dependencies.length > 0) {
                            handleDependencyChange(
                              formData.dependencies.length - 1, 
                              'type', 
                              e.target.value
                            );
                          }
                        }}
                      >
                        {DEPENDENCY_TYPES.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={2}>
                    <TextField
                      label="Lag (days)"
                      type="number"
                      size="small"
                      fullWidth
                      defaultValue={0}
                      InputProps={{ inputProps: { min: -30, max: 30 } }}
                      onChange={(e) => {
                        if (formData.dependencies.length > 0) {
                          handleDependencyChange(
                            formData.dependencies.length - 1, 
                            'lag', 
                            parseInt(e.target.value) || 0
                          );
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={2}>
                    <Button 
                      variant="contained" 
                      size="small" 
                      fullWidth
                      onClick={() => {
                        enqueueSnackbar('Select a task from the dropdown to add a dependency', { variant: 'info' });
                      }}
                    >
                      Add
                    </Button>
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    <strong>Dependency Types:</strong> FS = Finish to Start (most common), FF = Finish to Finish, SS = Start to Start, SF = Start to Finish
                  </Typography>
                </Box>
              </Paper>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button 
          onClick={onClose} 
          disabled={loading} 
          variant="outlined" 
          color="primary"
          sx={{ mr: 1 }}
        >
          Batal
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary" 
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {task ? 'Perbarui' : 'Simpan'}
        </Button>
      </DialogActions>
    </Dialog>
    
    {/* Task Split Dialog */}
    {task && (
      <TaskSplitDialog
        open={splitDialogOpen}
        onClose={handleCloseSplitDialog}
        task={task}
        onSplit={handleTaskSplit}
      />
    )}
  </>
  );
};

export default TaskForm;